import { isSameDay, subDays } from "date-fns";
import { useTranslation } from "react-i18next";
import React, {
	CSSProperties,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import { DayPicker, DateRange as RDPDateRange } from "@daypicker/react";
import { de, enUS, ja } from "@daypicker/react/locale";
import type { Locale } from "date-fns/locale";
import { DateRange } from "../data/date-range";
import { adjustDateEnd, adjustDateStart } from "../helper/date-helper";
import { classArr } from "../helper/style-helper";
import * as styles from "./date-range-selector.module.scss";
import TimePicker from "./time-picker";
import VelavuButton from "./velavu-button";

const PICKER_START_MONTH = new Date(2020, 0);
const SECONDS_PER_DAY = 24 * 60 * 60;

const DAY_PICKER_LOCALES: Record<string, Locale> = {
	en: enUS,
	de,
	ja,
};

/**
 * Returns the time value of a date in seconds since midnight
 */
function getTimeValue(date: Date | undefined): number | undefined {
	if (!date) {
		return date;
	}

	return date.getHours() * 3600 + date.getMinutes() * 60 + date.getSeconds();
}

/**
 * Returns a copy of a date with the time value set to the given value
 */
function applyTimeValue(date: Date, value: number): Date {
	const nextDate = new Date(date);
	const secondsSinceMidnight =
		((Math.trunc(value) % SECONDS_PER_DAY) + SECONDS_PER_DAY) %
		SECONDS_PER_DAY;
	const hours = Math.floor(secondsSinceMidnight / 3600);
	const minutes = Math.floor((secondsSinceMidnight % 3600) / 60);
	const seconds = secondsSinceMidnight % 60;

	nextDate.setHours(hours, minutes, seconds, 0);
	return nextDate;
}

export interface DateRangeSelectorProps {
	style?: CSSProperties;
	className?: string;

	range: DateRange;
	onApply: (range: DateRange) => void;
}

/**
 * A calendar that allows the user to select a range of dates
 */
export default function DateRangeSelector(props: DateRangeSelectorProps) {
	const { t, i18n } = useTranslation();
	const dayPickerLocale = useMemo(() => {
		const language = i18n.language.split("-")[0];
		return DAY_PICKER_LOCALES[language] ?? enUS;
	}, [i18n.language]);
	const [internalRange, setInternalRange] = useState<
		RDPDateRange | undefined
	>({
		from: props.range.start,
		to: props.range.end,
	});

	//Sync the props range to the internal range
	useEffect(() => {
		setInternalRange({
			from: props.range.start,
			to: props.range.end,
		});
	}, [props.range.start, props.range.end]);

	const maxDate = useMemo(() => new Date(), []);

	const propsOnApply = props.onApply;

	const startTime = useMemo(
		() => getTimeValue(internalRange?.from) ?? 0,
		[internalRange?.from],
	);
	const endTime = useMemo(
		() => getTimeValue(internalRange?.to) ?? SECONDS_PER_DAY - 1,
		[internalRange?.to],
	);

	const handleRangeSelect = useCallback(
		(range: RDPDateRange | undefined) => {
			setInternalRange((currentRange) => {
				// If the range is undefined, return the current range
				if (range === undefined) {
					return currentRange;
				}

				//Adjust the from and to dates to match the selected time
				let from = range.from;
				if (from !== undefined) {
					from = applyTimeValue(
						from,
						getTimeValue(currentRange?.from) ?? 0,
					);
				}

				let to = range.to;
				if (to !== undefined) {
					to = applyTimeValue(
						to,
						getTimeValue(currentRange?.to) ?? SECONDS_PER_DAY - 1,
					);
				}

				return {
					from,
					to,
				};
			});
		},
		[setInternalRange],
	);

	const handleStartTimeChange = useCallback((value: number) => {
		setInternalRange((range) => {
			if (range?.from === undefined) {
				return range;
			}

			return {
				...range,
				from: applyTimeValue(range.from, value),
			};
		});
	}, []);

	const setRangeLastFifteenMins = useCallback(() => {
		const start = new Date();
		start.setMinutes(start.getMinutes() - 15);
		setInternalRange({ from: start, to: new Date() });
	}, [setInternalRange]);
	const setRangeLastHour = useCallback(() => {
		const start = new Date();
		start.setHours(start.getHours() - 1);
		setInternalRange({ from: start, to: new Date() });
	}, [setInternalRange]);
	const setRangeLastDay = useCallback(() => {
		const start = new Date();
		start.setDate(start.getDate() - 1);
		setInternalRange({ from: start, to: new Date() });
	}, [setInternalRange]);
	const setRangeLastWeek = useCallback(() => {
		const today = new Date();
		setInternalRange({
			from: adjustDateStart(subDays(today, 7)),
			to: adjustDateEnd(today),
		});
	}, [setInternalRange]);
	const setRangeLastMonth = useCallback(() => {
		const start = new Date();
		start.setMonth(start.getMonth() - 1);
		const end = new Date();
		setInternalRange({
			from: adjustDateStart(start),
			to: adjustDateEnd(end),
		});
	}, [setInternalRange]);

	const handleEndTimeChange = useCallback((value: number) => {
		setInternalRange((range) => {
			if (range?.to === undefined) {
				return range;
			}

			return {
				...range,
				to: applyTimeValue(range.to, value),
			};
		});
	}, []);

	// Try to convert the internal range to a DateRange, or null if the range is invalid
	const selectedDateRange = useMemo((): DateRange | null => {
		if (
			internalRange === undefined ||
			internalRange.from === undefined ||
			internalRange.to === undefined
		) {
			return null;
		}
		return {
			start: new Date(internalRange.from),
			end: new Date(internalRange.to),
		};
	}, [internalRange]);

	const hasInvalidTimeRange = useMemo(() => {
		return (
			selectedDateRange !== null &&
			isSameDay(selectedDateRange.start, selectedDateRange.end) &&
			selectedDateRange.start.getTime() >= selectedDateRange.end.getTime()
		);
	}, [selectedDateRange]);

	const isDirty = useMemo(() => {
		return (
			selectedDateRange !== null &&
			(selectedDateRange.start.getTime() !==
				props.range.start.getTime() ||
				selectedDateRange.end.getTime() !== props.range.end.getTime())
		);
	}, [selectedDateRange, props.range]);

	const handleApply = useCallback(() => {
		if (selectedDateRange !== null && !hasInvalidTimeRange) {
			propsOnApply(selectedDateRange);
		}
	}, [hasInvalidTimeRange, propsOnApply, selectedDateRange]);

	return (
		<div
			style={props.style}
			className={classArr(styles.calendar, props.className)}
		>
			<DayPicker
				locale={dayPickerLocale}
				mode="range"
				selected={internalRange}
				onSelect={handleRangeSelect}
				fixedWeeks
				showOutsideDays
				captionLayout="dropdown"
				startMonth={PICKER_START_MONTH}
				today={maxDate}
				disabled={{ after: maxDate }}
			/>

			<div className={styles.footer}>
				<div className={styles.timeFields}>
					<label className={styles.timeField}>
						<span className={styles.footerLabel}>
							{t("elements.date_range.start")}
						</span>
						<TimePicker
							value={startTime}
							onChange={handleStartTimeChange}
							invalid={hasInvalidTimeRange}
						/>
					</label>
					<label className={styles.timeField}>
						<span className={styles.footerLabel}>
							{t("elements.date_range.end")}
						</span>
						<TimePicker
							value={endTime}
							onChange={handleEndTimeChange}
							invalid={hasInvalidTimeRange}
						/>
					</label>
				</div>

				<div className={styles.presetRow}>
					<div className={styles.footerLabel}>
						{t("elements.date_range.last_label")}
					</div>
					<button
						className={styles.footerLink}
						onClick={setRangeLastFifteenMins}
					>
						{t("elements.date_range.presets.15_min")}
					</button>
					<button
						className={styles.footerLink}
						onClick={setRangeLastHour}
					>
						{t("elements.date_range.presets.1_hr")}
					</button>
					<button
						className={styles.footerLink}
						onClick={setRangeLastDay}
					>
						{t("elements.date_range.presets.day")}
					</button>
					<button
						className={styles.footerLink}
						onClick={setRangeLastWeek}
					>
						{t("elements.date_range.presets.week")}
					</button>
					<button
						className={styles.footerLink}
						onClick={setRangeLastMonth}
					>
						{t("elements.date_range.presets.month")}
					</button>
				</div>

				<VelavuButton
					label={t("common.apply")}
					className={styles.applyButton}
					fullWidth
					onClick={handleApply}
					disabled={
						selectedDateRange === null ||
						hasInvalidTimeRange ||
						!isDirty
					}
				/>
			</div>
		</div>
	);
}
